import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';

import { QuestionItem } from '../QuestionItem.tsx';
import type { Question } from './BaseQuestionsList.tsx';
import { macroI18n } from './questionsListMacroShared.tsx';

type BaseQuestionsListMacroProps = {
	questions: Question[];
	loading: boolean;
	error?: any;
	limit: number;
	routerName: string;
	defaultLimit?: number;
};

const styles = cssMap({
	scrollable: {
		maxHeight: '800px',
		overflowY: 'auto',
	},
	centerContainer: {
		textAlign: 'center',
		minHeight: '200px',
	},
});

export const BaseQuestionsListMacro: React.FC<BaseQuestionsListMacroProps> = ({
	questions,
	loading,
	error,
	limit,
	routerName,
	defaultLimit = 10,
}) => {
	if (loading) {
		return (
			<Box xcss={styles.centerContainer}>
				<Spinner size="medium" label="Loading" />
			</Box>
		);
	}

	if (error || !questions?.length) {
		return (
			<Box xcss={styles.centerContainer}>
				<Text>
					<FormattedMessage {...macroI18n.noQuestionsFound} />
				</Text>
			</Box>
		);
	}

	// Ensure we always have a valid limit for scroll calculation
	const effectiveLimit = limit || defaultLimit;
	const shouldScroll = effectiveLimit > 9;

	const content = (
		<Stack space="space.250" role="list">
			{questions.map((question) => (
				<QuestionItem
					key={question.content.id}
					createdBy={question.content.history.createdBy.displayName}
					numberOfAnswers={question.content.answersTotalCount}
					numberOfVotes={question.content.numberOfVotes}
					createdDate={new Date(question.content.history.createdDate)}
					questionTitle={question.content.title}
					spaceKey={question.content.space.key}
					spaceName={question.content.space?.name}
					routerName={routerName}
					questionId={question.content.id}
					topics={question.content.labels}
					hasAcceptedAnswer={question.content.hasAcceptedAnswer}
				/>
			))}
		</Stack>
	);

	if (shouldScroll) {
		return (
			<Box
				xcss={styles.scrollable}
				paddingInlineEnd="space.100"
				testId="scrollable-questions-container"
			>
				{content}
			</Box>
		);
	}

	return <Box testId="questions-container">{content}</Box>;
};
